import { useEffect, useRef } from "react";

let gmapsPromise = null;
function loadGoogleMaps(key) {
  if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
  if (gmapsPromise) return gmapsPromise;
  gmapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existing) {
      const t0 = Date.now();
      const iv = setInterval(() => {
        if (window.google && window.google.maps) { clearInterval(iv); resolve(window.google.maps); }
        else if (Date.now() - t0 > 15000) { clearInterval(iv); reject(new Error("maps load timeout")); }
      }, 150);
      return;
    }
    const cbName = "__gmapsReadyCmp";
    window[cbName] = () => resolve(window.google.maps);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async&callback=${cbName}`;
    script.async = true;
    script.defer = true;
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return gmapsPromise;
}

// Geocode a free-text address to {lat,lng} using Google's geocoder (India-biased).
export async function geocodeAddress(address) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key || !address) return null;
  const maps = await loadGoogleMaps(key);
  const geocoder = new maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ address, componentRestrictions: { country: "in" } }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else resolve(null);
    });
  });
}

// Reverse-geocode lat/lng to a human address using Google's geocoder.
export async function reverseGeocode(lat, lng) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key || lat == null || lng == null) return null;
  const maps = await loadGoogleMaps(key);
  const geocoder = new maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ location: { lat: Number(lat), lng: Number(lng) } }, (results, status) => {
      if (status === "OK" && results && results[0]) resolve(results[0].formatted_address);
      else resolve(null);
    });
  });
}

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
const esc = (s) => String(s || "").replace(/</g, "&lt;").replace(/"/g, "&quot;");
const parseRate = (s) => { if (s == null) return null; const n = parseFloat(String(s).replace(/[^0-9.]/g, "")); return Number.isFinite(n) && n > 0 ? n : null; };
const SUBJECT_COLOR = "#1D4ED8";
const NO_RATE_COLOR = "#9CA3AF";
const PHOTO_COLOR = "#EA580C";
const TYPE_COLORS = {
  "Residential": "#0EA5E9", "Commercial": "#D97706", "Industrial": "#7C3AED",
  "Agricultural": "#16A34A", "Plot / Land": "#0891B2", "Under Construction": "#DB2777", "Mixed Use": "#CA8A04",
};
const TYPE_FALLBACK = ["#2563EB", "#D97706", "#7C3AED", "#16A34A", "#0891B2", "#DB2777", "#CA8A04", "#DC2626"];
function typeColorFor(ptype, order) {
  if (!ptype) return NO_RATE_COLOR;
  if (TYPE_COLORS[ptype]) return TYPE_COLORS[ptype];
  const idx = (order || []).indexOf(ptype);
  return TYPE_FALLBACK[(idx >= 0 ? idx : 0) % TYPE_FALLBACK.length];
}

// green (low) -> amber (mid) -> red (high)
function heatColor(t) {
  const lerp = (a, b, k) => Math.round(a + (b - a) * k);
  let r, g, b;
  if (t <= 0.5) { const k = t / 0.5; r = lerp(21, 245, k); g = lerp(128, 158, k); b = lerp(61, 11, k); }
  else { const k = (t - 0.5) / 0.5; r = lerp(245, 220, k); g = lerp(158, 38, k); b = lerp(11, 38, k); }
  return `rgb(${r},${g},${b})`;
}
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

export default function ComparablesMap({ subject, listings = [], manual = [], photoPins = [], onPick, colorBy = "rate" }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoRef = useRef(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const points = [];
  if (subject && num(subject.lat) != null && num(subject.lon) != null) {
    points.push({ kind: "subject", lat: num(subject.lat), lng: num(subject.lon), title: "Subject property", rate: subject.rate, rateNum: parseRate(subject.rate), area: subject.area, sub: "This valuation" });
  }
  listings.forEach((l) => {
    const la = num(l.lat), lo = num(l.lng);
    if (la != null && lo != null) points.push({ kind: "listing", lat: la, lng: lo, title: l.title || "Online listing", rate: l.rate, rateNum: parseRate(l.rate), area: l.area, url: l.url || null, blacklisted: !!l.blacklisted, ptype: l.ptype || null, sub: `${l.source || "Online"}${l.distance ? " · " + l.distance : ""}` });
  });
  manual.forEach((m) => {
    const la = num(m.lat), lo = num(m.lng);
    if (la != null && lo != null) points.push({ kind: "manual", lat: la, lng: lo, title: m.locality || "Manual comparable", rate: m.rate, rateNum: parseRate(m.rate), area: m.area, sub: m.note || "Manually added" });
  });
  photoPins.forEach((p) => {
    const la = num(p.lat), lo = num(p.lon);
    if (la != null && lo != null) points.push({ kind: "photo", lat: la, lng: lo, title: p.caption || `Site photo #${p.num}`, rate: "", rateNum: null, area: "", sub: "Site photo location (from photo GPS)", num: p.num });
  });

  const rated = points.filter((p) => p.kind !== "subject" && p.rateNum != null).map((p) => p.rateNum);
  const minR = rated.length ? Math.min(...rated) : 0;
  const maxR = rated.length ? Math.max(...rated) : 0;
  const sortedR = [...rated].sort((a, b) => a - b);
  const median = sortedR.length ? (sortedR.length % 2 ? sortedR[(sortedR.length - 1) / 2] : (sortedR[sortedR.length / 2 - 1] + sortedR[sortedR.length / 2]) / 2) : null;
  const isOutlier = (p) => p.kind !== "subject" && p.rateNum != null && median != null && rated.length >= 3 && Math.abs(p.rateNum - median) / median > 0.3;
  const anyOutlier = points.some(isOutlier);
  const typeList = [...new Set(points.filter((p) => (p.kind === "listing" || p.kind === "manual") && p.ptype).map((p) => p.ptype))];
  const hasUnspec = points.some((p) => (p.kind === "listing" || p.kind === "manual") && !p.ptype);
  const colorFor = (p) => {
    if (p.blacklisted) return "#111827";
    if (p.kind === "subject") return SUBJECT_COLOR;
    if (p.kind === "photo") return PHOTO_COLOR;
    if (colorBy === "type" && (p.kind === "listing" || p.kind === "manual")) return typeColorFor(p.ptype, typeList);
    if (p.rateNum == null) return NO_RATE_COLOR;
    const t = maxR > minR ? (p.rateNum - minR) / (maxR - minR) : 0.5;
    return heatColor(t);
  };

  useEffect(() => {
    if (!apiKey || !elRef.current) return;
    let cancelled = false;
    loadGoogleMaps(apiKey).then((maps) => {
      if (cancelled || !elRef.current) return;
      if (!mapRef.current) {
        mapRef.current = new maps.Map(elRef.current, { center: { lat: 21.25, lng: 81.63 }, zoom: 12, mapTypeId: "hybrid", streetViewControl: false, scrollwheel: false, gestureHandling: "cooperative", styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }] });
        infoRef.current = new maps.InfoWindow();
        mapRef.current.addListener("click", (e) => { if (onPickRef.current) onPickRef.current(e.latLng.lat(), e.latLng.lng()); });
      }
      const map = mapRef.current;
      markersRef.current.forEach((mk) => mk.setMap(null));
      markersRef.current = [];
      const bounds = new maps.LatLngBounds();
      points.forEach((p) => {
        const color = colorFor(p);
        const outlier = isOutlier(p);
        const mk = new maps.Marker({
          position: { lat: p.lat, lng: p.lng }, map, title: p.title, draggable: p.kind === "subject" && !!onPickRef.current,
          zIndex: p.kind === "subject" ? 999 : (p.kind === "photo" ? 400 : (outlier ? 500 : 1)),
          icon: { path: maps.SymbolPath.CIRCLE, scale: p.kind === "subject" ? 9 : (outlier ? 8.5 : 7), fillColor: color, fillOpacity: 1, strokeColor: outlier ? "#111827" : "#ffffff", strokeWeight: outlier ? 3.5 : 2 },
          label: p.kind === "photo" ? { text: String(p.num), color: "#ffffff", fontSize: "9px", fontWeight: "700" } : undefined,
        });
        mk.addListener("click", () => {
          const rateLine = p.rate && p.rate !== "\u2014" ? `Rate: <b>${esc(p.rate)}</b>${p.area && p.area !== "\u2014" ? " · " + esc(p.area) : ""}` : (p.area && p.area !== "\u2014" ? esc(p.area) : "");
          const link = p.url ? `<br/><a href="${esc(p.url)}" target="_blank" rel="noreferrer" style="color:#1d4ed8;font-weight:600">View listing \u2197</a>` : "";
          const flag = outlier ? `<br/><span style="color:#b91c1c;font-weight:600">\u26a0 Rate outlier vs area median</span>` : "";
          infoRef.current.setContent(`<div style="font:13px system-ui;min-width:150px"><b>${esc(p.title)}</b><br/>${rateLine}<br/><span style="color:#666">${esc(p.sub)}</span>${flag}${link}</div>`);
          infoRef.current.open(map, mk);
        });
        if (p.kind === "subject" && onPickRef.current) mk.addListener("dragend", (e) => onPickRef.current(e.latLng.lat(), e.latLng.lng()));
        markersRef.current.push(mk);
        bounds.extend({ lat: p.lat, lng: p.lng });
      });
      if (points.length === 1) { map.setCenter({ lat: points[0].lat, lng: points[0].lng }); map.setZoom(16); }
      else if (points.length > 1) { map.fitBounds(bounds, 60); }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, JSON.stringify(points)]);

  if (!apiKey) {
    return (
      <div data-testid="comparables-map" className="flex items-center justify-center text-xs text-muted-foreground border rounded-lg bg-muted/30" style={{ height: 320, width: "100%" }}>
        Map unavailable — Google Maps API key not configured.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="comparables-map">
      <div ref={elRef} style={{ height: 340, width: "100%", borderRadius: 8, overflow: "hidden", zIndex: 0 }} />
      <div className="flex items-center gap-4 flex-wrap text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border border-white shadow" style={{ background: SUBJECT_COLOR }} /> Subject</span>
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border border-white shadow" style={{ background: "#111827" }} /> Black-listed / caution</span>
        {photoPins.length > 0 && <span className="inline-flex items-center gap-1" data-testid="comparables-map-photo-legend"><span className="h-3 w-3 rounded-full border border-white shadow" style={{ background: PHOTO_COLOR }} /> Site photo GPS ({photoPins.length})</span>}
        {colorBy === "type" && (typeList.length > 0 || hasUnspec) ? (
          <span className="inline-flex items-center gap-3 flex-wrap" data-testid="comparables-map-type-legend">
            {typeList.map((t) => (<span key={t} className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border border-white shadow" style={{ background: typeColorFor(t, typeList) }} /> {t}</span>))}
            {hasUnspec && <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border border-white shadow" style={{ background: NO_RATE_COLOR }} /> Unspecified</span>}
          </span>
        ) : rated.length > 0 ? (
          <span className="inline-flex items-center gap-2">
            Rate <span>{inr(minR)}</span>
            <span className="h-3 w-24 rounded-full" style={{ background: "linear-gradient(90deg, rgb(21,128,61), rgb(245,158,11), rgb(220,38,38))" }} />
            <span>{inr(maxR)}/sq.ft</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full" style={{ background: NO_RATE_COLOR }} /> Comparables (no rate)</span>
        )}
        <span className="ml-auto" data-testid="comparables-map-count">{points.length} point(s) plotted</span>
      </div>
      {anyOutlier && <p className="text-[10px] text-rose-700" data-testid="comparables-map-outlier-note">⚠ Dark-ringed markers are rate outliers (more than 30% from the area median) — worth double-checking.</p>}
    </div>
  );
}
